import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import {
  AdminProductSetOrder,
  BaseProductSet,
  Building,
  CompactBuildingUnitWithCustomer,
  CompactUser,
  Listing,
  PayoutAccount,
  ProductItem,
  ProductSet,
  SavedWorkspace,
  Updatable,
  User,
  VendorPayoutAccount,
  addOrUpdateInList,
  safeAssign,
  sortByDate,
  updateInList
} from 'shared';
import { VendorUser } from 'types';
import {
  AdminAIAssistantConversation,
  AiAssistantIntent,
  AiAssistantLearning,
  ConversationDetailData,
  DashboardData
} from 'ux/Admin/AIAssistant/types';
import { AdminListBuilding } from 'ux/Admin/Buildings/types';
import {
  AdminConsumablesOrderListItem,
  AdminConsumablesOrderPaymentListItem,
  AdminConsumablesProduct
} from 'ux/Admin/Consumables/types';
import { AdminListOfficeLead, AdminListServicesRequest, AdminListTour } from 'ux/Admin/Customer/Leads/types';
import { AdminDetailCustomer, AdminListCustomer, AdminListPlan, AdminListReferral } from 'ux/Admin/Customers/types';
import { AdminDetailLicensor } from 'ux/Admin/Licensors/Licensor/types';
import { AdminListLicensor } from 'ux/Admin/Licensors/types';
import { AdminDetailShortTermBooking, AdminListShortTermBooking } from 'ux/Admin/ShortTermBookings/types';
import { AdminSupportTicketListItem } from 'ux/Admin/Support/types';
import { AdminTaskListRow, AdminTaskScheduleListRow } from 'ux/Admin/Tasks/types';
import { AdminAvailableService } from 'ux/Admin/Tenants/Tenant/TenantInfo/types';
import { AdminTenancyList } from 'ux/Admin/Tenants/types';
import { AdminReferralConfig } from 'ux/Admin/Tools/AdminReferralConfigs/types';
import { FeatureFlag, GlobalFeatureFlag } from 'ux/Admin/Tools/Features/types';
import { ListSearchLocation } from 'ux/Admin/Tools/Locations/types';
import { AdminProduct } from 'ux/Admin/Tools/ProductsOld/types';
import { AdminListUser } from 'ux/Admin/Users/types';
import { AdminDetailVendor } from 'ux/Admin/Vendors/Vendor/types';
import { AdminListVendor } from 'ux/Admin/Vendors/types';
import { AdminListWorkspace } from 'ux/Admin/Workspaces/types';
import { AdminStartupRequest } from 'ux/Admin/api_requests';

interface ReduxListSearchLocation extends ListSearchLocation {
  id: string;
}

export type AdminState = {
  buildings: AdminListBuilding[];
  buildingUnits: CompactBuildingUnitWithCustomer[];
  building: Building | null;
  unassignedBuildings: AdminListBuilding[] | null;
  tenants: AdminTenancyList[];
  workspaces: AdminListWorkspace[];
  workspace: SavedWorkspace | null;
  users: AdminListUser[];
  licensors: AdminListLicensor[];
  licensor: AdminDetailLicensor | null;
  vendors: AdminListVendor[];
  vendorUsers: VendorUser[];
  vendor: AdminDetailVendor | null;
  user: User | null;
  customers: AdminListCustomer[];
  customer: AdminDetailCustomer | null;
  officeLeads: AdminListOfficeLead[];
  servicesRequests: AdminListServicesRequest[];
  plans: AdminListPlan[];
  tours: AdminListTour[];
  products: AdminProduct[] | null;
  mapWorkspaces: Listing[];
  tasks: AdminTaskListRow[];
  taskReporters: CompactUser[];
  taskSchedules: AdminTaskScheduleListRow[];
  shortTermBookings: AdminListShortTermBooking[];
  global: AdminStartupRequest | null;
  locations: ReduxListSearchLocation[];
  referrals: AdminListReferral[];
  referralConfigs: AdminReferralConfig[];
  featureFlags: FeatureFlag[];
  globalFeatureFlags: GlobalFeatureFlag[];
  aiAssistantConversations: AdminAIAssistantConversation[];
  aiAssistantConversationsCount: number | null;
  aiAssistantDashboard: DashboardData | null;
  aiAssistantConversationDetail: ConversationDetailData | null;
  aiAssistantLearnings: AiAssistantLearning[];
  aiAssistantIntents: AiAssistantIntent[];
  aiAssistantDeveloperImprovements: any[];
  aiAssistantFeedbacks: any[];
  supportTickets: AdminSupportTicketListItem[];
  availableServices: AdminAvailableService[];
  productSets: ProductSet[];
  baseProductSets: BaseProductSet[];
  productItems: ProductItem[];
  consumables: AdminConsumablesProduct[];
  productSetOrders: AdminProductSetOrder[];
  consumablesOrders: AdminConsumablesOrderListItem[];
  consumablesOrderPayments: AdminConsumablesOrderPaymentListItem[];
};

const initialState: AdminState = {
  aiAssistantConversations: [],
  aiAssistantConversationsCount: null,
  aiAssistantDashboard: null,
  aiAssistantConversationDetail: null,
  aiAssistantLearnings: [],
  aiAssistantIntents: [],
  aiAssistantDeveloperImprovements: [],
  aiAssistantFeedbacks: [],
  buildings: [],
  buildingUnits: [],
  unassignedBuildings: null,
  tenants: [],
  building: null,
  workspaces: [],
  mapWorkspaces: [],
  workspace: null,
  users: [],
  user: null,
  licensors: [],
  licensor: null,
  vendors: [],
  vendor: null,
  vendorUsers: [],
  customers: [],
  customer: null,
  officeLeads: [],
  plans: [],
  tours: [],
  products: [],
  tasks: [],
  taskReporters: [],
  taskSchedules: [],
  shortTermBookings: [],
  global: null,
  locations: [],
  referrals: [],
  referralConfigs: [],
  featureFlags: [],
  globalFeatureFlags: [],
  supportTickets: [],
  availableServices: [],
  productSets: [],
  baseProductSets: [],
  productItems: [],
  consumables: [],
  servicesRequests: [],
  productSetOrders: [],
  consumablesOrders: [],
  consumablesOrderPayments: []
};

const adminSlice = createSlice({
  name: 'Admin',
  initialState,
  reducers: {
    setAiAssistantConversations: (state, { payload }: PayloadAction<AdminAIAssistantConversation[]>) => {
      state.aiAssistantConversations = payload;
    },
    setAiAssistantConversationsCount: (state, { payload }: PayloadAction<number>) => {
      state.aiAssistantConversationsCount = payload;
    },
    setAiAssistantDashboard: (state, { payload }: PayloadAction<DashboardData>) => {
      state.aiAssistantDashboard = payload;
    },
    setAiAssistantConversationDetail: (state, { payload }: PayloadAction<ConversationDetailData | null>) => {
      state.aiAssistantConversationDetail = payload;
    },
    setAiAssistantLearnings: (state, { payload }: PayloadAction<AiAssistantLearning[]>) => {
      state.aiAssistantLearnings = payload;
    },
    setAiAssistantIntents: (state, { payload }: PayloadAction<AiAssistantIntent[]>) => {
      state.aiAssistantIntents = payload;
    },
    createAiAssistantLearning: (state, { payload }: PayloadAction<AiAssistantLearning>) => {
      state.aiAssistantLearnings = [payload, ...state.aiAssistantLearnings];
    },
    updateAiAssistantLearning: (state, { payload }: PayloadAction<Updatable<AiAssistantLearning>>) => {
      updateInList(state.aiAssistantLearnings, payload);
    },
    setAiAssistantDeveloperImprovements: (state, { payload }: PayloadAction<any[]>) => {
      state.aiAssistantDeveloperImprovements = payload;
    },
    setAiAssistantFeedbacks: (state, { payload }: PayloadAction<any[]>) => {
      state.aiAssistantFeedbacks = payload;
    },
    setBuildings: (state, { payload }: PayloadAction<AdminListBuilding[]>) => {
      state.buildings = payload;
    },
    setUnassignedBuildings: (state, { payload }: PayloadAction<AdminListBuilding[]>) => {
      state.unassignedBuildings = payload;
    },
    setBuildingUnits: (state, { payload }: PayloadAction<CompactBuildingUnitWithCustomer[]>) => {
      state.buildingUnits = payload;
    },
    updateBuildingById: (state, { payload }: PayloadAction<Updatable<AdminListBuilding>>) => {
      updateInList(state.buildings, payload);
    },
    setWorkspaces: (state, { payload }: PayloadAction<AdminListWorkspace[]>) => {
      state.workspaces = payload;
    },
    setWorkspace: (state, { payload }: PayloadAction<SavedWorkspace | null>) => {
      state.workspace = payload;
    },
    updateWorkspace: (state, { payload }: PayloadAction<Partial<SavedWorkspace>>) => {
      safeAssign(state.workspace, payload);
    },
    updateWorkspaceById: (state, { payload }: PayloadAction<Updatable<AdminListWorkspace>>) => {
      updateInList(state.workspaces, payload);
    },
    setUsers: (state, { payload }: PayloadAction<AdminListUser[]>) => {
      state.users = payload;
    },
    updateUserById: (state, { payload }: PayloadAction<Updatable<AdminListUser>>) => {
      updateInList(state.users, payload);
    },
    updateListUser: (state, { payload }: PayloadAction<Updatable<AdminListUser>>) => {
      updateInList(state.users, payload);
    },
    setProductSets: (state, { payload }: PayloadAction<ProductSet[]>) => {
      state.productSets = payload;
    },
    setBaseProductSets: (state, { payload }: PayloadAction<BaseProductSet[]>) => {
      state.baseProductSets = payload;
    },
    updateBaseProductSetById: (state, { payload }: PayloadAction<Updatable<BaseProductSet>>) => {
      updateInList(state.baseProductSets, payload);
    },
    setProductItems: (state, { payload }: PayloadAction<ProductItem[]>) => {
      state.productItems = payload;
    },
    setConsumablesProducts: (state, { payload }: PayloadAction<AdminConsumablesProduct[]>) => {
      state.consumables = payload;
    },
    updateConsumablesProductById: (state, { payload }: PayloadAction<Updatable<AdminConsumablesProduct>>) => {
      updateInList(state.consumables, payload);
    },
    setConsumablesOrders: (state, { payload }: PayloadAction<AdminConsumablesOrderListItem[]>) => {
      state.consumablesOrders = payload;
    },
    setConsumablesOrderPayments: (state, { payload }: PayloadAction<AdminConsumablesOrderPaymentListItem[]>) => {
      state.consumablesOrderPayments = payload;
    },
    setProductSetOrders: (state, { payload }: PayloadAction<AdminProductSetOrder[]>) => {
      state.productSetOrders = payload;
    },
    setUser: (state, { payload }: PayloadAction<User | null>) => {
      state.user = payload;
    },
    updateUser: (state, { payload }: PayloadAction<Updatable<User>>) => {
      safeAssign(state.user, payload);
    },
    setLicensors: (state, { payload }: PayloadAction<AdminListLicensor[]>) => {
      state.licensors = payload;
    },
    setLicensor: (state, { payload }: PayloadAction<AdminDetailLicensor | null>) => {
      state.licensor = payload;
    },
    setLicensorPayoutAccount: (state, { payload }: PayloadAction<PayoutAccount>) => {
      if (!state.licensor) return;

      state.licensor.payout_account = payload;
    },
    setTaskReporters: (state, { payload }: PayloadAction<CompactUser[]>) => {
      state.taskReporters = payload;
    },
    setVendors: (state, { payload }: PayloadAction<AdminListVendor[]>) => {
      state.vendors = payload;
    },
    setVendor: (state, { payload }: PayloadAction<AdminDetailVendor | null>) => {
      state.vendor = payload;
    },
    setVendorUsers: (state, { payload }: PayloadAction<VendorUser[]>) => {
      state.vendorUsers = payload;
    },
    setVendorPayoutAccount: (state, { payload }: PayloadAction<VendorPayoutAccount>) => {
      if (!state.vendor) return;

      state.vendor.vendor_payout_account = payload;
    },
    setCustomers: (state, { payload }: PayloadAction<AdminListCustomer[]>) => {
      state.customers = payload;
    },
    setCustomer: (state, { payload }: PayloadAction<AdminDetailCustomer | null>) => {
      state.customer = payload;
    },
    setOfficeLeads: (state, { payload }: PayloadAction<AdminListOfficeLead[]>) => {
      state.officeLeads = payload;
    },
    setServicesRequests: (state, { payload }: PayloadAction<AdminListServicesRequest[]>) => {
      state.servicesRequests = payload;
    },
    setPlans: (state, { payload }: PayloadAction<AdminListPlan[]>) => {
      state.plans = payload;
    },
    setTours: (state, { payload }: PayloadAction<AdminListTour[]>) => {
      state.tours = payload;
    },
    setReferrals: (state, { payload }: PayloadAction<AdminListReferral[]>) => {
      state.referrals = payload;
    },
    setReferralConfigs: (state, { payload }: PayloadAction<AdminReferralConfig[]>) => {
      state.referralConfigs = payload;
    },
    updateReferralConfig: (state, { payload }: PayloadAction<Updatable<AdminReferralConfig>>) => {
      updateInList(state.referralConfigs, payload);
    },
    updateStb: (state, { payload }: PayloadAction<AdminDetailShortTermBooking>) => {
      if (!state.customer) return;

      const { short_term_bookings } = state.customer;

      updateInList(short_term_bookings, payload);
    },
    updateCustomerById: (state, { payload }: PayloadAction<Updatable<AdminListCustomer>>) => {
      updateInList(state.customers, payload);
    },
    setProducts: (state, { payload }: PayloadAction<AdminProduct[]>) => {
      state.products = payload;
    },
    updateProductById: (state, { payload }: PayloadAction<AdminProduct>) => {
      if (!state.products) {
        state.products = [payload];
      } else {
        updateInList(state.products || [], payload);
      }
    },
    setMapWorkspaces: (state, { payload }: PayloadAction<Listing[]>) => {
      state.mapWorkspaces = payload;
    },
    setTasks: (state, { payload }: PayloadAction<AdminTaskListRow[]>) => {
      state.tasks = payload;
    },
    addOrUpdateTask: (state, { payload }: PayloadAction<AdminTaskListRow>) => {
      const existingTaskIndex = state.tasks.findIndex(r => r.id === payload.id);

      if (existingTaskIndex > -1) {
        state.tasks[existingTaskIndex] = payload;
      } else {
        state.tasks.push(payload);
      }
      state.tasks.sort((r1, r2) => sortByDate(r1.max_due_date, r2.max_due_date));
    },
    removeTask: (state, { payload }: PayloadAction<{ id: number }>) => {
      state.tasks = state.tasks.filter(r => r.id !== payload.id);
    },
    addOrUpdateTaskSchedule: (state, { payload }: PayloadAction<AdminTaskScheduleListRow>) => {
      addOrUpdateInList(state.taskSchedules, payload);
    },
    setTaskSchedules: (state, { payload }: PayloadAction<AdminTaskScheduleListRow[]>) => {
      state.taskSchedules = payload;
    },
    setShortTermBookings: (state, { payload }: PayloadAction<AdminListShortTermBooking[]>) => {
      state.shortTermBookings = payload;
    },
    setGlobal: (state, { payload }: PayloadAction<AdminStartupRequest>) => {
      state.global = payload;
    },
    setLocations: (state, { payload }: PayloadAction<ListSearchLocation[]>) => {
      state.locations = payload.map(p => ({ ...p, id: p.guid }));
    },
    updateLocation: (state, { payload }: PayloadAction<Updatable<ReduxListSearchLocation>>) => {
      updateInList(state.locations, payload);
    },
    setFeatureFlags: (state, { payload }: PayloadAction<FeatureFlag[]>) => {
      state.featureFlags = payload;
    },
    setGlobalFeatureFlags: (state, { payload }: PayloadAction<GlobalFeatureFlag[]>) => {
      state.globalFeatureFlags = payload;
    },
    setSupportTickets: (state, { payload }: PayloadAction<AdminSupportTicketListItem[]>) => {
      state.supportTickets = payload;
    },
    updateAdminSupportTicket: (state, { payload }: PayloadAction<AdminSupportTicketListItem>) => {
      updateInList(state.supportTickets, payload);
    },
    addSupportTicket: (state, { payload }: PayloadAction<AdminSupportTicketListItem>) => {
      state.supportTickets.push(payload);
    },
    setAvailableServices: (state, { payload }: PayloadAction<AdminAvailableService[]>) => {
      state.availableServices = payload;
    },
    setTenants: (state, { payload }: PayloadAction<AdminTenancyList[]>) => {
      state.tenants = payload;
    }
  }
});

export const { actions } = adminSlice;

export default adminSlice.reducer;
